import * as ko from 'knockout';
import { TeamBase } from '../../shared/models/team-base';
import type { IGameRecords } from '../../shared/interfaces/IGameRecords';
import type { IGame } from '../../shared/interfaces/IGame';
import { RankMethod } from '../../shared/enums/rank-method';
import { MlbGameRecords } from './game-records';
import { Enumerable } from '../../shared/linq';

export class MlbTeam extends TeamBase {
  ConferenceGamesBack: KnockoutObservable<number>;
  DivisionGamesBack: KnockoutObservable<number>;
  IsDivisionWinner: KnockoutObservable<boolean>;
  GroupedSchedule: KnockoutComputed<KnockoutObservable<IGame>[]>;
  subscribers: KnockoutSubscription[] = [];

  constructor(
    teamAbbreviation: string,
    regionName: string,
    teamName: string,
    conference: string,
    division: string,
    teamID: number,
    teamLogo?: string
  ) {
    super(teamAbbreviation, regionName, teamName, conference, division, teamID, teamLogo);

    this.ConferenceGamesBack = ko.observable(0);
    this.DivisionGamesBack = ko.observable(0);

    this.IsDivisionWinner = ko.observable(false);

    this.GroupedSchedule = ko.computed(() => {
      this.FakeObs();
      let gameId = 1;
      const retArray: KnockoutObservable<IGame>[] = [];
      const groups = Enumerable.From(this.Schedule())
        .Where((p: KnockoutObservable<IGame>) => typeof p === 'function')
        .GroupBy((p: KnockoutObservable<IGame>) => p().week)
        .OrderBy((p: any) => p.Key())
        .ToArray();
      for (const group of groups) {
        const games = Enumerable.From(group.source)
          .OrderBy((p: KnockoutObservable<IGame>) => p().SortOrder())
          .ToArray() as KnockoutObservable<IGame>[];
        for (const game of games) {
          game().gameNum(gameId);
          gameId++;
          retArray.push(game);
        }
      }
      return retArray;
    });
  }

  protected override createGameRecords(): IGameRecords {
    return new MlbGameRecords(
      this.Wins,
      this.Losses,
      this.Ties,
      this.TeamName,
      this.FakeObs
    );
  }

  protected override createConferenceCss(): string {
    this.FakeObs();
    if (this.ConferenceRank() <= 6) {
      return 'in-post-season';
    }
    return 'out-post-season';
  }

  protected override createRankMethodText(method?: RankMethod): string {
    switch (method ?? this.RankMethod()) {
      case RankMethod.AllPCT:
        return 'Pct vs League';
      case RankMethod.ConfPCT:
        return 'Pct vs Conference';
      case RankMethod.DivPCT:
        return 'Pct vs Div';
      case RankMethod.SOS:
        return 'Strength of Schedule';
      case RankMethod.SOV:
        return 'Strength of Victory';
      case RankMethod.DivTop_AllPCT:
        return 'Top of Division - Pct vs League';
      case RankMethod.DivTop_DivPCT:
        return 'Top of Division - Pct vs Div';
      case RankMethod.DivTop_ConfPCT:
        return 'Top of Division - Pct vs Conference';
      case RankMethod.DivTop_SOS:
        return 'Top of Division - Strength of Schedule';
      case RankMethod.DivTop_SOV:
        return 'Top of Division - Strength of Victory';
      case RankMethod.DivTop_HTH:
        return 'Top of Division - Head to Head';
      case RankMethod.HTH:
        return 'Head-to-Head Tiebreaker';
      case RankMethod.CG:
        return 'Common Games';
      case RankMethod.CoinFlip:
        return 'Coin Flip';
      case RankMethod.CommonGames:
        return 'Common Games';
      case RankMethod.CommonPct:
        return 'Common Pct';
      case RankMethod.DivisionWinner:
        return 'Division Winner';
      case RankMethod.DivisionWinnerMultiWay:
        return 'Multi Way - Division Winner';
      case RankMethod.ConfPctFullBreakMultiWay:
        return 'Multi Way - Full Break (Conference%)';
      case RankMethod.DivPctFullBreakMultiWay:
        return 'Multi Way - Full Break (Division%)';
      case RankMethod.TwoWayConfPCT:
        return 'Two Way - Conf%';
      case RankMethod.TwoWayDivPCT:
        return 'Two Way - Div%';
      case RankMethod.HTHPartialBreakMultiWay:
        return 'Multi Way - Partial Break (HTH)';
      case RankMethod.HTHTwoWay:
        return 'Two Way - HTH';
      case RankMethod.HTHFullBreakMultiWay:
        return 'Multi Way - HTH Full Break';
      case RankMethod.DivisionWinnerCoinFlip:
        return 'Division Winner (Coin Flip)';
      case RankMethod.NonDivisionWinner:
        return 'Non-Division Winner';
      case RankMethod.DivPctPartialBreakMultiWay:
        return 'Multi Way - Partial Break (Div%)';
      default:
        return '';
    }
  }

  override initSubscribers(): void {
    const self = this;
    if (!self.isInit) {
      self.subscribers.push(
        self.Losses.subscribe((changes) => {
          changes.forEach((change) => {
            if (change.status === 'added') {
              self.Losses(
                Enumerable.From(self.Losses())
                  .Distinct((p: IGame) => p.gameGuid)
                  .ToArray()
              );
              self.Wins.remove((p: IGame) => p.gameGuid === change.value.gameGuid);
            }
          });
        }, null, 'arrayChange')
      );

      self.subscribers.push(
        self.Wins.subscribe((changes) => {
          changes.forEach((change) => {
            if (change.status === 'added') {
              self.Wins(
                Enumerable.From(self.Wins())
                  .Distinct((p: IGame) => p.gameGuid)
                  .ToArray()
              );
              self.Losses.remove((p: IGame) => p.gameGuid === change.value.gameGuid);
            }
          });
        }, null, 'arrayChange')
      );

      self.GameRecords.initSubscribers();
      self.isInit = true;
    }
  }

  override reset(): void {
    this.SOV(0);
    this.SOS(0);
    this.Wins([]);
    this.Losses([]);
    this.Ties([]);
    for (const subscriber of this.subscribers) {
      subscriber.dispose();
    }
    this.subscribers = [];
    for (const subscriber of (this.GameRecords as MlbGameRecords).subscribers) {
      subscriber.dispose();
    }
    (this.GameRecords as MlbGameRecords).subscribers = [];
    this.isInit = false;
    this.ConferenceRank(null);
    this.Schedule = ko.observable(new Array<KnockoutObservable<IGame>>());
    this.DraftOrderRankMethod(null);
    this.ConferenceGamesBack(null);
    this.DivisionGamesBack(null);
  }
}
